"use client";
import { Api } from "@/Apis";
import {
  LandingBlogGetDetailExtraOutputApiModel,
  LandingBlogGetDetailOutputApiModel,
  LandingSiteTemplateGetDataOutputApiModel,
} from "@/Apis/ClinicApi";
import { LandingCommentListPage } from "@/Areas/Landing/Pages/LandingComment";
import { Html, If, SmartImage, SmartLink } from "@/Components";
import { Constants } from "@/Helpers";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { Faq } from "@/Templates/Doccure/Sections";
import { defaultFaqSetting } from "@/Templates/Doccure/Sections/Faq";
import { Space } from "antd";
import _ from "lodash";
import moment from "moment-jalaali";
import { FC, useEffect, useState } from "react";
import { FiEye, FiMessageSquare } from "react-icons/fi";

interface LandingBlogDetailPageProps {
  result: LandingBlogGetDetailOutputApiModel;
  layoutData: LandingSiteTemplateGetDataOutputApiModel;
}

const LandingBlogDetailPage: FC<LandingBlogDetailPageProps> = ({
  result,
  layoutData,
}) => {
  const [commentsCount, setCommentsCount] = useState(0);
  const [extraData, setExtraData] =
    useState<LandingBlogGetDetailExtraOutputApiModel | null>(null);
  const defaultBlogImageUrl = `${Constants.ArchiveSiteAddress}/File/Default/Blog.png`;

  useEffect(() => {
    const controller = new AbortController();

    const loadExtraData = async () => {
      try {
        const r = await Api.LandingBlog.getDetailExtra(
          { Id: result.Id },
          controller.signal,
        );
        if (r.Success && r.Result) setExtraData(r.Result);
      } catch {
        // ignore abort and transient request failures
      }
    };

    void loadExtraData();
    return () => controller.abort();
  }, [result.Id]);

  return (
    <div className="blog-view">
      <h3 className="mb-3">{result.Title}</h3>
      <div className="blog blog-single-post">
        <div className="blog-image">
          <a>
            <SmartImage
              cover
              alt={result.Image?.AltText ?? result.Title}
              src={result.Image?.Url ?? defaultBlogImageUrl}
              className="img-fluid"
              width={824}
              height={400}
              objectFit="contain"
            />
          </a>
        </div>
        <div className="blog-info d-md-flex align-items-center justify-content-between flex-wrap">
          <div className="post-left">
            <ul>
              <li>
                <SmartLink href={result.Category.Link}>
                  <span className="badge badge-dark fs-14 fw-medium">
                    {result.Category.Name}
                  </span>
                </SmartLink>
              </li>
              <li>
                <i className="isax isax-calendar" />
                {moment(result.DateRegister).format("jDD jMMMM jYYYY")}
              </li>
              {result.CenterUserAuthor && (
                <li>
                  <div className="post-author">
                    <SmartLink href={result.CenterUserAuthor.Link}>
                      <Space>
                        <SmartImage
                          cover
                          src={result.CenterUserAuthor.ImageUrl}
                          alt={result.CenterUserAuthor.FullName}
                          width={24}
                          height={24}
                        />{" "}
                        <span>{result.CenterUserAuthor.FullName}</span>
                      </Space>
                    </SmartLink>
                  </div>
                </li>
              )}
            </ul>
          </div>
          <div className="blog-views d-flex align-items-center justify-content-md-end">
            <span className="badge badge-outline-dark me-2">
              <FiMessageSquare className="me-1" />
              {commentsCount}
            </span>
            <span className="badge badge-outline-primary">
              <FiEye className="me-1" />
              {result.Visits}
            </span>
          </div>
        </div>
        <div className="blog-content">
          <Html value={result.Content} />
        </div>
      </div>
      {result.CenterUserAuthor && (
        <>
          <h4 className="mb-3">درباره نویسنده</h4>
          <div className="about-author">
            <div className="about-author-img">
              <div className="author-img-wrap">
                <SmartLink href={result.CenterUserAuthor.Link}>
                  <SmartImage
                    cover
                    src={result.CenterUserAuthor.ImageUrl}
                    alt={result.CenterUserAuthor.FullName}
                    width={118}
                    height={118}
                  />
                </SmartLink>
              </div>
            </div>
            <div className="author-details">
              <Html value={result.CenterUserAuthor.Bio} />
            </div>
          </div>
        </>
      )}
      <If condition={!_.isEmpty(result.Tags)}>
        <h4 className="mb-3">برچسب ها</h4>
        <div className="d-flex align-items-center flex-wrap blog-tags gap-3 mb-4">
          {result.Tags?.map((tag) => (
            <SmartLink key={tag.Id} href={tag.Link} className="badge">
              {tag.Name}
            </SmartLink>
          ))}
        </div>
      </If>
      <If
        condition={
          !_.isEmpty(
            result.Categories.filter((c) => c.Id != result.Category.Id),
          )
        }
      >
        <h4 className="mb-3">دسته بندی ها</h4>
        <div className="d-flex align-items-center flex-wrap blog-tags gap-3 mb-4">
          {result.Categories.map((tag) => (
            <SmartLink key={tag.Id} href={tag.Link} className="badge">
              {tag.Name}
            </SmartLink>
          ))}
        </div>
      </If>
      <LandingCommentListPage
        TypeKey={CenterOptions.Enum.CommentType.Blog.key}
        BlogId={result.Id}
        onGetList={(list) => setCommentsCount(list.length)}
      />
      <If condition={!_.isEmpty(extraData?.CenterFaqs)}>
        <Faq
          pageData={layoutData}
          dataSource={{ Items: extraData?.CenterFaqs ?? [] }}
          setting={defaultFaqSetting}
        />
      </If>
      {/* <If condition={!_.isEmpty(result.RelatedBlogs)}>
            <LandingBlogsCard title="بلاگ های مرتبط" model={result.RelatedBlogs ?? []} />
        </If> */}
    </div>
  );
};

export default LandingBlogDetailPage;
